import { useEffect, useState, ReactNode, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useCognitoAuth, MFA_VERIFIED_KEY } from "@/hooks/useCognitoAuth";
import { emailFromIdToken } from "@/lib/cognito";
import { getMfaStatus, verifyMfa } from "@/lib/mfa";
import { withBase } from "@/lib/basePath";
import { canUseV2WorkspaceAdmin } from "@/lib/internalAccess";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";

// --- TEMPORARY private-preview gate (/v2 build ONLY) ------------------------
// The new admin ships to two places: the apex build ("/admin", open to all
// customers) and the "/v2" preview build ("/v2/admin", still being built). While
// it's in preview the /v2 build's admin is restricted to the SAME pilot list +
// scheduled cutover the rest of the app uses (canUseV2WorkspaceAdmin, in
// lib/internalAccess) — one source of truth, so this gate can't drift out of sync
// with the WorkspaceLayout pilot gate and the classic-admin banner (which is what
// let pilot users see the banner yet get stuck on "private preview" here). Any
// other signed-in user gets a "private preview" notice; the apex admin and all
// public routes are unaffected, and the data APIs still enforce their own Cognito
// auth — this is only a UI gate on the preview channel.
// ----------------------------------------------------------------------------

// App-level MFA gate for photohawk-web only. When a signed-in user has MFA enabled and hasn't passed
// the challenge this browser session, it renders the code prompt instead of the app. This never runs
// for FTP or the studio app (they authenticate straight against Cognito and don't load this app).
type Phase = "pass" | "checking" | "challenge";

export default function RequireMfa({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user, tokens } = useCognitoAuth();
  const email = (emailFromIdToken(tokens.idToken) || user?.signInDetails?.loginId || user?.username || "") as string;

  // The MFA gate only guards the admin. Public routes (marketing home, /pages,
  // /galleries, login, …) must render immediately — never block them on an MFA
  // status check (that flashed a full-screen spinner on the marketing home for
  // signed-in users). The router basename is stripped, so pathname is
  // app-relative ("/admin/…") in both the root and the /v2 builds.
  const { pathname } = useLocation();
  // NEVER gate the login/seed route. `/admin/login` is the token-handoff landing
  // (classic → v2): it establishes the session, so rendering the MFA/preview gate
  // over it interrupts the seed→navigate flow and bounces the user back to a fresh
  // login. Everything else under /admin is gated as normal.
  const isLoginRoute = pathname === "/admin/login" || pathname.endsWith("/admin/login");
  // The buyer's "Edit your reel" and WhatsApp status pages live under /admin (the only path a tenant host sends here) but are public:
  // a signed-in photographer opening a buyer's link must not hit the MFA gate either.
  const isBuyerRoute = ["/admin/reel", "/admin/whatsapp"].some((r) => pathname === r || pathname.endsWith(r));
  const gated = !isLoginRoute && !isBuyerRoute && (pathname === "/admin" || pathname.startsWith("/admin/"));

  const [phase, setPhase] = useState<Phase>("pass");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!gated) { setPhase("pass"); return; }
    if (isLoading) return;
    if (!isAuthenticated) { setPhase("pass"); return; }
    // Already verified this browser session for this user?
    if (email && sessionStorage.getItem(MFA_VERIFIED_KEY) === email) { setPhase("pass"); return; }

    setPhase("checking");
    getMfaStatus()
      .then((s) => { if (!cancelled) setPhase(s.enabled ? "challenge" : "pass"); })
      // Fail open on a status error so a transient hiccup can't lock everyone out (this is an
      // app-access gate, not a per-request barrier — the data APIs still require the Cognito token).
      .catch(() => { if (!cancelled) setPhase("pass"); });
    return () => { cancelled = true; };
  }, [gated, isAuthenticated, isLoading, email]);

  const submit = useCallback(async () => {
    if (code.length < 6 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await verifyMfa(code);
      if (email) sessionStorage.setItem(MFA_VERIFIED_KEY, email);
      setPhase("pass");
    } catch (e) {
      setError(e instanceof Error ? e.message : "That code isn't valid.");
      setCode("");
    } finally {
      setSubmitting(false);
    }
  }, [code, submitting, email]);

  // Private-preview gate: a signed-in user who isn't on the admin allowlist gets
  // a notice instead of the admin. Public routes (gated === false) are unaffected.
  // Only gate the /v2 PREVIEW build — apex "/admin" stays open to all customers.
  // BASE_URL is baked in at build time by Vite: "/v2/" in the preview build, "/"
  // in the apex build. The router basename is stripped, so `pathname` is "/admin"
  // in BOTH builds — so the gate must key on the build, not the path.
  const isPreviewChannel = import.meta.env.BASE_URL !== "/";
  const blocked =
    isPreviewChannel && gated && !isLoading && isAuthenticated && !!email && !canUseV2WorkspaceAdmin(email);

  if (blocked) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Private preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              The new Photohawk admin is in private preview and isn’t available on
              your account yet. Please check back soon.
            </p>
            <Button className="w-full" onClick={() => { window.location.href = withBase("/"); }}>
              Back to site
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === "pass") return <>{children}</>;

  if (phase === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // challenge
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Two-factor authentication</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app to continue.
          </p>
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={submit}>
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          {error && <p className="text-sm text-destructive text-center">{error}</p>}
          <Button className="w-full" onClick={submit} disabled={code.length < 6 || submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
